/* ==========================================================================
   pdp-base.js — JS compartido de las PDP independientes (skill pdp-independiente)
   Vanilla, sin dependencias, un solo IIFE. Se carga con `defer` desde sections/pdp-theme.liquid.
   Módulos:
     1. Selector de packs (delegación de eventos; varias buybox por página)
     2. Flujo de checkout heredado de UroControl (clear → add → waitForLasso ≤3000 ms → proceedToCheckout)
        con fallback /checkout y fallback final /cart/<variant>:1?checkout
     3. Tracking Meta: InitiateCheckout con eventID `pdp-<variantId>-<timestamp>` justo antes del salto (+300 ms de margen)
     4. Sticky CTA móvil con IntersectionObserver sobre los botones principales
     5. Accordion accesible (details/summary, modo "solo uno abierto")
     6. Galería (thumbs → slide, slide activo → thumb)
   Datos de producto: window.__pdp (lo emite snippets/pdp-tracking.liquid en el <head>).
   ========================================================================== */
(function () {
  'use strict';
  if (window.__pdpBaseLoaded) return;
  window.__pdpBaseLoaded = true;

  var doc = document;
  var html = doc.documentElement;
  var PDP = window.__pdp || {};
  var ROUTES = PDP.routes || {};
  var CART_ADD = (ROUTES.cartAdd || '/cart/add') + '.js';
  var CART_CLEAR = (ROUTES.cartClear || '/cart/clear') + '.js';
  var CART_URL = ROUTES.cart || '/cart';
  var ROOT = ROUTES.root || '/';
  var LASSO_WAIT_MS = 3000;
  var IC_MARGIN_MS = 300;

  /* ---------- utilidades ---------- */
  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function toNumber(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }
  function rootPath(path) {
    // ROOT acaba en "/" (p. ej. "/en-us/"); path empieza sin "/"
    return ROOT.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
  }

  /* ---------- 1. Selector de packs ---------- */
  function getBuyboxes() { return qsa('[data-pdp-buybox]'); }
  function primaryBuybox() { return qs('[data-pdp-buybox][data-primary="true"]') || getBuyboxes()[0] || null; }

  function selectedPack(box) {
    if (!box) return null;
    return qs('.pdp-pack.is-active[data-variant-id]', box) || qs('.pdp-pack[data-variant-id]:not(.is-unavailable)', box);
  }

  function activatePack(pack) {
    if (!pack || pack.classList.contains('is-unavailable')) return;
    var box = pack.closest('[data-pdp-buybox]');
    if (!box) return;
    qsa('.pdp-pack', box).forEach(function (p) {
      var on = p === pack;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-checked', on ? 'true' : 'false');
      p.setAttribute('tabindex', on ? '0' : '-1');
    });
    box.dataset.selectedVariant = pack.dataset.variantId;
    // Sincronizar el mismo pack en las otras buybox (hero y CTA final comparten producto)
    if (!pack.__syncing) {
      getBuyboxes().forEach(function (other) {
        if (other === box) return;
        var twin = qs('.pdp-pack[data-variant-id="' + pack.dataset.variantId + '"]', other);
        if (twin && !twin.classList.contains('is-active')) { twin.__syncing = true; activatePack(twin); twin.__syncing = false; }
      });
    }
    updateSticky(pack);
    doc.dispatchEvent(new CustomEvent('pdp:pack-change', { detail: packData(pack) }));
  }

  function packData(pack) {
    return {
      variantId: pack.dataset.variantId,
      title: pack.dataset.title || '',
      price: toNumber(pack.dataset.price),
      compareAt: toNumber(pack.dataset.compareAt),
      priceFormatted: pack.dataset.priceFormatted || '',
      compareFormatted: pack.dataset.compareFormatted || '',
      currency: pack.dataset.currency || PDP.currency || 'USD'
    };
  }

  doc.addEventListener('click', function (e) {
    var pack = e.target.closest('.pdp-pack[data-variant-id]');
    if (pack) { activatePack(pack); return; }
    var btn = e.target.closest('[data-pdp-checkout]');
    if (btn) { e.preventDefault(); onCheckoutClick(btn); return; }
    var scroll = e.target.closest('[data-pdp-scroll]');
    if (scroll) {
      var target = qs(scroll.getAttribute('data-pdp-scroll')) || primaryBuybox();
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }
  });

  doc.addEventListener('keydown', function (e) {
    var pack = e.target.closest && e.target.closest('.pdp-pack[data-variant-id]');
    if (!pack) return;
    var box = pack.closest('[data-pdp-buybox]');
    var packs = qsa('.pdp-pack:not(.is-unavailable)', box);
    var i = packs.indexOf(pack);
    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); activatePack(pack); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); var n = packs[(i + 1) % packs.length]; activatePack(n); n.focus(); }
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); var p = packs[(i - 1 + packs.length) % packs.length]; activatePack(p); p.focus(); }
  });

  function initBuyboxes() {
    getBuyboxes().forEach(function (box) {
      var active = qs('.pdp-pack.is-active[data-variant-id]', box);
      if (!active || active.classList.contains('is-unavailable')) active = qs('.pdp-pack[data-variant-id]:not(.is-unavailable)', box);
      if (active) activatePack(active);
    });
  }

  /* ---------- 3. Tracking ---------- */
  function fbqReady() { return typeof window.fbq === 'function'; }

  function trackInitiateCheckout(data) {
    if (!fbqReady()) return null;
    if (data._eventID) return data._eventID; // una sola vez por intento de checkout
    var eventID = 'pdp-' + data.variantId + '-' + Date.now();
    data._eventID = eventID;
    try {
      window.fbq('track', 'InitiateCheckout', {
        value: data.price,
        currency: data.currency,
        content_ids: [String(data.variantId)],
        contents: [{ id: String(data.variantId), quantity: 1 }],
        content_type: 'product',
        num_items: 1
      }, { eventID: eventID });
    } catch (err) { /* nunca bloquear el checkout por el pixel */ }
    return eventID;
  }

  window.__pdpTrack = {
    initiateCheckout: trackInitiateCheckout,
    fbqReady: fbqReady
  };

  /* ---------- 2. Flujo de checkout ---------- */
  function waitForLasso(maxMs) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function check() {
        if (typeof window.proceedToCheckout === 'function') return resolve(true);
        if (Date.now() - start > maxMs) return resolve(false);
        setTimeout(check, 100);
      })();
    });
  }

  function closeDrawers() {
    qsa('cart-drawer, cart-drawer-component, cart-notification, #CartDrawer, .cart-drawer, dialog[open]').forEach(function (el) {
      try {
        if (typeof el.close === 'function') el.close();
        el.removeAttribute('open');
        el.classList.remove('active', 'is-open', 'open', 'animate', 'is-visible');
      } catch (err) { /* noop */ }
    });
  }

  var checkingOut = false;

  function onCheckoutClick(btn) {
    if (checkingOut) return;
    var box = null;
    var boxSel = btn.getAttribute('data-pdp-checkout');
    if (boxSel) box = qs(boxSel);
    if (!box) box = btn.closest('[data-pdp-buybox]');
    if (!box) box = primaryBuybox();
    var pack = selectedPack(box);
    if (!pack) return;
    startCheckout(packData(pack), btn);
  }

  function startCheckout(data, btn) {
    checkingOut = true;
    var buttons = qsa('[data-pdp-checkout]');
    var labels = buttons.map(function (b) { return b.textContent; });
    var busyText = (btn && btn.getAttribute('data-busy-text')) || 'PROCESSING...';
    buttons.forEach(function (b) { b.disabled = true; b.classList.add('is-busy'); b.textContent = busyText; });
    html.classList.add('pdp-checking-out');
    var closeTimer = setInterval(closeDrawers, 50);

    function restore() {
      clearInterval(closeTimer);
      html.classList.remove('pdp-checking-out');
      buttons.forEach(function (b, i) { b.disabled = false; b.classList.remove('is-busy'); b.textContent = labels[i]; });
      checkingOut = false;
    }

    var variantId = Number(data.variantId);
    var flow = (async function () {
      /* 1. Vaciar carrito: solo viaja el pack elegido */
      await fetch(CART_CLEAR, { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      /* 2. Añadir la variante por AJAX (Lasso intercepta /cart/add y sincroniza su carrito) */
      var res = await fetch(CART_ADD, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!res.ok) throw new Error('add-to-cart failed: ' + res.status);
      /* 3. Esperar a Lasso y saltar. El InitiateCheckout va JUSTO antes del salto. */
      var lassoReady = await waitForLasso(LASSO_WAIT_MS);
      /* Margen de ~300 ms para que el pixel envíe el IC antes de que la navegación lo corte (ref 07 §2) */
      if (trackInitiateCheckout(data)) await new Promise(function (r) { setTimeout(r, IC_MARGIN_MS); });
      clearInterval(closeTimer);
      if (lassoReady) {
        window.proceedToCheckout();
      } else {
        window.location.href = rootPath('checkout');
      }
    })();

    flow.catch(function () {
      /* Fallback final: permalink nativo de Shopify directo a checkout */
      restore();
      trackInitiateCheckout(data);
      window.location.href = rootPath('cart/' + data.variantId + ':1?checkout');
    });
  }

  /* ---------- 4. Sticky CTA ---------- */
  var sticky = null;

  function updateSticky(pack) {
    if (!sticky) sticky = qs('[data-pdp-sticky]');
    if (!sticky || !pack) return;
    var price = qs('[data-sticky-price]', sticky);
    var compare = qs('[data-sticky-compare]', sticky);
    var title = qs('[data-sticky-title]', sticky);
    if (price) price.textContent = pack.dataset.priceFormatted || '';
    if (compare) {
      compare.textContent = pack.dataset.compareFormatted || '';
      compare.hidden = !pack.dataset.compareFormatted;
    }
    if (title && sticky.getAttribute('data-sticky-title-mode') === 'pack') title.textContent = pack.dataset.title || '';
  }

  function initSticky() {
    sticky = qs('[data-pdp-sticky]');
    if (!sticky) return;
    var targets = qsa('[data-pdp-buybox] [data-pdp-checkout]');
    if (!targets.length || !('IntersectionObserver' in window)) return;
    var visible = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) visible.add(en.target); else visible.delete(en.target); });
      sticky.classList.toggle('is-visible', visible.size === 0);
    }, { threshold: 0 });
    targets.forEach(function (t) { io.observe(t); });
    var active = selectedPack(primaryBuybox());
    if (active) updateSticky(active);
  }

  /* ---------- 5. Accordion ---------- */
  function initAccordions() {
    qsa('[data-pdp-accordion]').forEach(function (acc) {
      var single = acc.getAttribute('data-single') === 'true';
      acc.addEventListener('toggle', function (e) {
        var item = e.target;
        if (!item.open || !single) return;
        qsa('details[open]', acc).forEach(function (d) { if (d !== item) d.open = false; });
      }, true);
    });
  }

  /* ---------- 6. Galería ---------- */
  function initGalleries() {
    qsa('[data-pdp-gallery]').forEach(function (g) {
      var track = qs('.pdp-gallery__track', g);
      var slides = qsa('.pdp-gallery__slide', g);
      var thumbs = qsa('.pdp-gallery__thumb', g);
      if (!track || slides.length < 2) return;
      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          var s = slides[i];
          if (s) track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        });
      });
      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var i = slides.indexOf(en.target);
          thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === i); });
          qsa('video', en.target).forEach(function (v) { if (v.autoplay) v.play().catch(function () {}); });
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach(function (s) { io.observe(s); });
    });
  }

  /* ---------- arranque ---------- */
  function init() {
    initBuyboxes();
    initSticky();
    initAccordions();
    initGalleries();
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  /* Editor de temas: re-inicializar al recargar una sección */
  doc.addEventListener('shopify:section:load', function () { sticky = null; init(); });
})();
